import React, { useEffect, useRef, useState } from 'react';
import { Lock, FileSpreadsheet } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { registerExcelPasswordPrompt } from '@/lib/excel';
import { useApp } from '@/context/AppContext';

export const ExcelPasswordDialog = () => {
  const { currentUser } = useApp();
  const [req, setReq] = useState(null);
  const [pwd, setPwd] = useState('');
  const resolver = useRef(null);
  const uid = useRef(null);
  uid.current = currentUser?.id;

  useEffect(() => registerExcelPasswordPrompt(({ fileName, error }) => new Promise((resolve) => {
    resolver.current = resolve;
    setPwd('');
    setReq({ fileName, error });
  }), () => uid.current || ''), []);

  const finish = (value) => { setReq(null); resolver.current?.(value); resolver.current = null; };

  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) finish(null); }}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="excel-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-bold flex items-center gap-2"><Lock className="w-4 h-4 text-amber-600" /> Password-protected Excel</DialogTitle>
        </DialogHeader>
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (pwd) finish(pwd); }}>
          <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="truncate" data-testid="excel-password-filename">{req?.fileName}</span>
          </div>
          <p className="text-xs text-slate-500">This workbook is encrypted. Enter its open password to unlock and continue the upload. The password is used once and never stored.</p>
          <Input autoFocus type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Workbook password" className="h-9 text-sm" data-testid="excel-password-input" />
          {req?.error && <p className="text-xs font-semibold text-rose-600" data-testid="excel-password-error">{req.error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => finish(null)} data-testid="excel-password-cancel">Cancel</Button>
            <Button type="submit" size="sm" disabled={!pwd} className="text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="excel-password-submit">Unlock & continue</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
